import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Banknote, Loader2, RotateCcw, Search, CheckCircle2, XCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { refundsApi, type RefundResponse, withdrawalApi, type WithdrawalResponse } from "@/lib/api";
import { formatNaira } from "@/data/venues";

export const Route = createFileRoute("/admin/pending-payouts")({
  component: PendingPayoutsPage,
});

const statusColors: Record<string, string> = {
  pending: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
  approved: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
  rejected: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300",
  processed: "bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300",
};

function fmtDate(d: string) {
  return new Date(d).toLocaleDateString("en-NG", { day: "2-digit", month: "short", year: "numeric" });
}

type PayoutItem =
  | { kind: "refund"; data: RefundResponse }
  | { kind: "withdrawal"; data: WithdrawalResponse };

function PendingPayoutsPage() {
  const [items, setItems] = useState<PayoutItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [processingId, setProcessingId] = useState<string | null>(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [refundsRes, withdrawals] = await Promise.all([
        refundsApi.list({ status: "pending", pageSize: 100 }),
        withdrawalApi.list("pending"),
      ]);
      const refundItems: PayoutItem[] = refundsRes.data.map((r) => ({ kind: "refund", data: r }));
      const withdrawalItems: PayoutItem[] = withdrawals.map((w) => ({ kind: "withdrawal", data: w }));
      const combined = [...refundItems, ...withdrawalItems].sort(
        (a, b) => new Date(b.data.createdAt).getTime() - new Date(a.data.createdAt).getTime()
      );
      setItems(combined);
    } catch {
      toast.error("Failed to load pending payouts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const handleRefund = async (id: string, status: "approved" | "rejected") => {
    setProcessingId(id);
    try {
      await refundsApi.process(id, { status });
      toast.success(`Refund marked as ${status}`);
      fetchAll();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Update failed";
      toast.error(msg);
    } finally {
      setProcessingId(null);
    }
  };

  const handleWithdrawal = async (id: string, approved: boolean) => {
    let remark: string | undefined;
    if (!approved) {
      remark = prompt("Reason for rejecting this withdrawal?") ?? undefined;
      if (remark === undefined) return; // user cancelled the prompt
    }
    setProcessingId(id);
    try {
      await withdrawalApi.approve(id, approved, remark);
      toast.success(`Withdrawal ${approved ? "approved" : "rejected"}`);
      fetchAll();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Update failed";
      toast.error(msg);
    } finally {
      setProcessingId(null);
    }
  };

  const filtered = items.filter((item) => {
    const s = search.toLowerCase();
    if (!s) return true;
    if (item.kind === "refund") {
      const r = item.data;
      return (
        r.refundRef.toLowerCase().includes(s) ||
        r.bookingRef.toLowerCase().includes(s) ||
        (r.userName?.toLowerCase() ?? "").includes(s) ||
        (r.bankName?.toLowerCase() ?? "").includes(s)
      );
    }
    const w = item.data;
    return (
      (w.applicantName?.toLowerCase() ?? "").includes(s) ||
      (w.accountName?.toLowerCase() ?? "").includes(s) ||
      (w.bankName?.toLowerCase() ?? "").includes(s)
    );
  });

  const pendingCount = items.length;
  const totalAmount = items.reduce((sum, item) => sum + item.data.amount, 0);

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4 sm:p-6 lg:p-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold sm:text-3xl">Pending Payouts</h1>
          <p className="text-sm text-muted-foreground">
            Refunds and wallet withdrawals waiting for manual bank transfer.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="secondary" className="gap-1.5">
            <RotateCcw className="size-3" /> {pendingCount} pending
          </Badge>
          <Badge variant="outline" className="gap-1.5">
            <Banknote className="size-3" /> {formatNaira(totalAmount)}
          </Badge>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search payouts..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 w-64"
          />
        </div>
        <Button variant="outline" size="sm" onClick={fetchAll}>
          <RotateCcw className="size-3.5 mr-1" /> Refresh
        </Button>
        <Link to="/admin/refunds" className="ml-auto text-sm font-medium text-primary hover:underline inline-flex items-center gap-1">
          All refunds <ArrowRight className="size-3.5" />
        </Link>
        <Link to="/admin/withdrawals" className="text-sm font-medium text-primary hover:underline inline-flex items-center gap-1">
          All withdrawals <ArrowRight className="size-3.5" />
        </Link>
      </div>

      {loading ? (
        <div className="flex h-40 items-center justify-center">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-border/60 bg-card py-20 text-center shadow-soft">
          <Banknote className="mb-3 size-12 text-muted-foreground/30" />
          <p className="font-semibold text-muted-foreground">No pending payouts</p>
          <p className="text-sm text-muted-foreground mt-1">Refund and withdrawal requests will appear here.</p>
        </div>
      ) : (
        <div className="rounded-2xl border border-border/60 bg-card shadow-soft overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-muted-foreground">
              <tr>
                <th className="px-4 py-3 text-left font-medium">Type / Ref</th>
                <th className="px-4 py-3 text-left font-medium">Recipient</th>
                <th className="px-4 py-3 text-left font-medium">Payout Account</th>
                <th className="px-4 py-3 text-left font-medium">Amount</th>
                <th className="px-4 py-3 text-left font-medium">Date</th>
                <th className="px-4 py-3 text-left font-medium">Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((item) => {
                const id = item.data.id;
                if (item.kind === "refund") {
                  const r = item.data;
                  return (
                    <tr key={id} className="border-t border-border/60 hover:bg-muted/30">
                      <td className="px-4 py-3">
                        <Badge variant="outline" className="text-xs">Refund</Badge>
                        <div className="font-medium mt-1">{r.refundRef}</div>
                        <div className="font-mono text-xs text-muted-foreground">{r.bookingRef}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium">{r.userName || "Guest"}</div>
                        <div className="text-xs text-muted-foreground">{r.userEmail}</div>
                        <div className="text-xs text-muted-foreground">{r.userPhone}</div>
                      </td>
                      <td className="px-4 py-3">
                        {r.bankName ? (
                          <>
                            <div className="font-medium">{r.bankName}</div>
                            <div className="font-mono text-xs">{r.accountNumber}</div>
                            <div className="text-xs text-muted-foreground">{r.accountName}</div>
                          </>
                        ) : (
                          <span className="text-xs text-muted-foreground">No account provided</span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-semibold">{formatNaira(r.amount)}</td>
                      <td className="px-4 py-3 text-muted-foreground">{fmtDate(r.createdAt)}</td>
                      <td className="px-4 py-3">
                        {processingId === id ? (
                          <Loader2 className="size-4 animate-spin" />
                        ) : (
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" className="h-8 gap-1 text-emerald-600" onClick={() => handleRefund(id, "approved")}>
                              <CheckCircle2 className="size-3.5" /> Paid out
                            </Button>
                            <Button size="sm" variant="outline" className="h-8 gap-1 text-red-600" onClick={() => handleRefund(id, "rejected")}>
                              <XCircle className="size-3.5" /> Reject
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                }
                const w = item.data;
                return (
                  <tr key={id} className="border-t border-border/60 hover:bg-muted/30">
                    <td className="px-4 py-3">
                      <Badge variant="outline" className="text-xs">Withdrawal</Badge>
                      <div className="font-medium mt-1">{w.applicantName || "Wallet"}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{w.applicantName || "—"}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{w.bankName ?? "—"}</div>
                      <div className="font-mono text-xs">{w.accountNumber ?? "—"}</div>
                      <div className="text-xs text-muted-foreground">{w.accountName ?? "—"}</div>
                    </td>
                    <td className="px-4 py-3 font-semibold">{formatNaira(w.amount)}</td>
                    <td className="px-4 py-3 text-muted-foreground">{fmtDate(w.createdAt)}</td>
                    <td className="px-4 py-3">
                      {processingId === id ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" className="h-8 gap-1 text-emerald-600" onClick={() => handleWithdrawal(id, true)}>
                            <CheckCircle2 className="size-3.5" /> Paid out
                          </Button>
                          <Button size="sm" variant="outline" className="h-8 gap-1 text-red-600" onClick={() => handleWithdrawal(id, false)}>
                            <XCircle className="size-3.5" /> Reject
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
